import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import ScrollToTop from "@/components/ScrollToTop";
import { ConfirmProvider } from "@/components/Confirm";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Messages from "@/pages/Messages";
import Tasks from "@/pages/Tasks";
import RescheduleTask from "@/pages/RescheduleTask";
import Events from "@/pages/Events";
import Integrations from "@/pages/Integrations";
import MagicLink from "@/pages/MagicLink";
import ApproveLogin from "@/pages/ApproveLogin";
import CalendarPage from "@/pages/CalendarPage";
import ReviewQueue from "@/pages/ReviewQueue";
import WhatsAppConnect from "@/pages/WhatsAppConnect";
import Settings from "@/pages/Settings";
import LeadSettings from "@/pages/LeadSettings";
import ChannelSettings from "@/pages/ChannelSettings";
import TeamSettings from "@/pages/TeamSettings";
import AcceptInvite from "@/pages/AcceptInvite";
import Leads from "@/pages/Leads";
import StreamHistory from "@/pages/StreamHistory";
import ChangePassword from "@/pages/ChangePassword";
import Admin from "@/pages/Admin";
import AdminLogin from "@/pages/AdminLogin";
import AdminForgotPassword from "@/pages/AdminForgotPassword";
import AdminResetPassword from "@/pages/AdminResetPassword";
import HomePage from "@/pages/marketing/HomePage";
import AboutPage from "@/pages/marketing/AboutPage";
import FeaturesPage from "@/pages/marketing/FeaturesPage";
import PricingPage from "@/pages/marketing/PricingPage";
import ContactPage from "@/pages/marketing/ContactPage";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen grid place-items-center text-slate-500 text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <ConfirmProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            {/* Marketing Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Auth Routes */}
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            {/* One-time sign-in link from WhatsApp notifications */}
            <Route path="/l/:token" element={<MagicLink />} />
            <Route path="/approve-login/:token" element={<ApproveLogin />} />

            {/* Team-invite acceptance — public so unauthenticated invitees can join */}
            <Route path="/invite/:token" element={<AcceptInvite />} />

            {/* App Routes */}
            <Route path="/app" element={<Protected><Layout /></Protected>}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="messages" element={<Messages />} />
              <Route path="stream-history" element={<StreamHistory />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="tasks/:id/reschedule" element={<RescheduleTask />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="events" element={<Events />} />
              <Route path="review" element={<ReviewQueue />} />
              <Route path="whatsapp" element={<WhatsAppConnect />} />
              <Route path="leads" element={<Leads />} />
              <Route path="settings" element={<Settings />} />
              <Route path="settings/leads" element={<LeadSettings />} />
              <Route path="settings/channel" element={<ChannelSettings />} />
              <Route path="settings/team" element={<TeamSettings />} />
              <Route path="settings/integrations" element={<Integrations />} />
              <Route path="settings/password" element={<ChangePassword />} />
            </Route>

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password/:token" element={<AdminResetPassword />} />
            <Route path="/admin" element={<Admin />} />

            {/* Fallback redirects */}
            <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ConfirmProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
